import { ComponentRendering } from '@sitecore-jss/sitecore-jss-react';
import { TDateField, TLinkField, TSitecoreContext } from '../../../types/sitecore.types';
import { ImageField, LinkField, StringField } from '../../../types/types';
import { appendRootToInternalLink } from '../../../utils/appendRootToInternalLink.utils';
import { appendRootToRichTextInternalLinks } from '../../../utils/appendRootToRichTextInternalLinks.utils';
import { TProductImageProps } from '../../@shared/picture/types/picture.types';
import { TFunction } from 'i18next';
import { SP_QUERY_STRINGS } from '../../../constants/special-offers-page.constants';
import {
  ContentHubSpecialOffersProps,
  ContentHubSpecialOffersRendererFields,
  ContentHubSpecialOffersRendererParams,
  ContentHubSpecialOffersSlideFields,
  TProductSpecialOffersBackgroundImage,
} from './fields.definitions';

export class ContentHubSpecialOffersSlideModel {
  private readonly card: ContentHubSpecialOffersSlideFields;
  private readonly itemIndex: number;
  private readonly t?: TFunction;
  private readonly specialOfferMainPageLink: string;

  constructor(Card:ContentHubSpecialOffersSlideFields, index,t?: TFunction, specialOfferMainPageLink?: string) {
    this.card = Card || null;
    this.itemIndex = index;
    this.t = t;
    this.specialOfferMainPageLink = specialOfferMainPageLink || '';
  }

  get id(): string {
    return this.card?.id ?? '';
  }

  get titleUrl(): string {
    return this.card?.titleUrl || '';
  }

  get title(): StringField {
    return { value: this.card?.formattedTitle };
  }

  get description(): StringField {
    return { value: appendRootToRichTextInternalLinks(this.card?.description) };
  }

  get disclaimer(): StringField {
    return { value: appendRootToRichTextInternalLinks(this.card?.disclaimer) };
  }

  get expiration(): TDateField {
    return { value: this.card?.expiration };
  }

  get type(): StringField {
    const defaultValue = { value: 'offerCardRegular' };
    if (this.itemIndex === 0) defaultValue.value = 'offerCardWide';
    return defaultValue;
  }

  get badge(): string {
    return this.card?.badge;
  }

  get expirationLabel(): StringField {
    return { value: this.card?.expirationLabel };
  }

  get image(): ImageField {
    return {
      value: {
        src: this.card?.assets ? this.card?.assets[0]?.full : undefined,
        alt: this.card?.assets ? this.card?.assets[0]?.alt : undefined,
      },
    };
  }

  get asset() {
    return this.card?.assets?.[0];
  }

  get productImageProps(): TProductImageProps {
    const imageProps: TProductImageProps = {};
    const desktopRendition = this.isCardWide ? this.asset?.rendition_desktop_size1000 : this.asset?.rendition_desktop_size500;
    const mobileRendition = this.asset?.rendition_mobile_size300;
    if (desktopRendition?.url || mobileRendition?.url) {
      imageProps['desktopRendition'] = desktopRendition;
      imageProps['mobileRendition'] = mobileRendition;
    } else {
      imageProps['desktopRendition'] = { url: this.isCardWide ? this.card?.defaultWideImage : this.card?.defaultNarrowImage };
      imageProps['mobileRendition'] = { url: this.card?.defaultNarrowImage };
    }
    return imageProps;
  }

  get buttonPrimary(): LinkField {
    if (!this.titleUrl || !this.specialOfferMainPageLink) return { value: { href: '', title: '' } };
    const title = this.t?.('cnhi-special-offer-learn-more');
    return {
      value: {
        href: `${this.specialOfferMainPageLink}/${this.titleUrl}`,
        title,
        text: title,
      },
    };
  }

  get buttonSecondary(): LinkField {
    return {
      value: {
        href: appendRootToInternalLink(this.card?.button2?.url),
        title: this.card?.button2?.label,
        target: this.card?.button2?.target,
      },
    };
  }

  get cardStyles(): StringField {
    return { value: this.card?.cardStyles };
  }

  get isCardWide(): boolean {
    return this.card?.cardStyles === 'Wide';
  }
}

export class ContentHubSpecialOffersModel {
  private readonly fields: null | ContentHubSpecialOffersRendererFields;
  private readonly params: null | ContentHubSpecialOffersRendererParams;
  private readonly rendering?: ComponentRendering;
  private readonly context?: TSitecoreContext;
  private readonly t?: TFunction;
  private readonly specialOfferMainPageLink: string;

  constructor(props: ContentHubSpecialOffersProps, t?: TFunction, specialOfferMainPageLink?: string) {
    this.fields = props?.fields || null;
    this.params = props?.params || null;
    this.rendering = props?.rendering;
    this.context = props?.sitecoreContext;
    this.t = t;
    this.specialOfferMainPageLink = specialOfferMainPageLink || '';
  }

  get thumbnailEE(): string {
    return this.fields?.itemData?.thumbnail || '';
  }

  get componentName(): string {
    return this.rendering?.componentName || '';
  }

  get eyebrow(): StringField {
    return { value: this.fields?.apiData?.eyebrow };
  }

  get title(): StringField {
    return { value: this.fields?.apiData?.title };
  }

  get description(): StringField {
    return { value: appendRootToRichTextInternalLinks(this.fields?.apiData?.description) };
  }

  getLink(lineId: string): TLinkField | undefined {
    if (!lineId || !this.specialOfferMainPageLink) return;
    const title = this.t?.('cnhi-special-offer-read-more') || '';
    return {
      value: {
        href: `${this.specialOfferMainPageLink}?${SP_QUERY_STRINGS.LINE}=${lineId}`,
        title,
        text: title,
      },
    };
  }

  get desktopImage(): TProductSpecialOffersBackgroundImage | undefined {
    return this.fields?.itemData?.fields?.imageParameters?.desktopImageData;
  }

  get mobileImage(): TProductSpecialOffersBackgroundImage | undefined {
    return this.fields?.itemData?.fields?.imageParameters?.mobileImageData;
  }

  get defaultBgDesktopImage(): TProductSpecialOffersBackgroundImage | undefined {
    return {
      url: this.context?.defaultBackgroundImageParameters?.desktopImageData?.specialOffers,
    };
  }

  get defaultBgMobileImage(): TProductSpecialOffersBackgroundImage | undefined {
    return {
      url: this.context?.defaultBackgroundImageParameters?.mobileImageData?.specialOffers,
    };
  }

  get desktopBgImage(): TProductSpecialOffersBackgroundImage | undefined {
    return this.desktopImage?.url ? this.desktopImage : this.defaultBgDesktopImage;
  }

  get mobileBgImage(): TProductSpecialOffersBackgroundImage | undefined {
    return this.mobileImage?.url ? this.mobileImage : this.defaultBgMobileImage;
  }

  get items() {
    const items: ContentHubSpecialOffersSlideFields[] = this.fields?.apiData?.items || [];
    const cardStyles = (this.params?.cardStyles && this.params?.cardStyles.split('|')) || [];
    const defaultWideImage = this.fields?.itemData?.fields?.defaultCardImageParameters?.specialOffersWideCardImageData?.url ?? '';
    const defaultNarrowImage = this.fields?.itemData?.fields?.defaultCardImageParameters?.specialOffersNarrowCardImageData?.url ?? '';
    const todayDate = new Date();
    const validSpecialOffers = items.filter((offer, index) => {
      offer['cardStyles'] = cardStyles[index];
      offer['defaultWideImage'] = defaultWideImage;
      offer['defaultNarrowImage'] = defaultNarrowImage;
      const expiryDate = new Date(offer.expiration || '');
      return expiryDate.setHours(0, 0, 0, 0) >= todayDate.setHours(0, 0, 0, 0);
    });
    const defaultValue =
      validSpecialOffers &&
      validSpecialOffers.map((Card, index) => {
        return new ContentHubSpecialOffersSlideModel(Card, index, this.t, this.specialOfferMainPageLink);
      });
    if (this.fields && this.fields['items']) {
      return this.fields['items'].map((Card, index) => {
        Card['defaultWideImage'] = defaultWideImage;
        Card['defaultNarrowImage'] = defaultNarrowImage;
        return new ContentHubSpecialOffersSlideModel(Card, index, this.t, this.specialOfferMainPageLink);
      });
    }
    return defaultValue;
  }

  get isUnreleased(): boolean {
    return !!this.fields?.isComponentUnreleased;
  }
}
